import axios from "axios";
import { DEFAULT_LANGUAGE, SUPPORTED_LANGUAGES } from "../constants/trans";
import { i18n } from "../plugins/i18n";

const LANG_BASE_URL = `${process.env.VUE_APP_LANG_HOST}/${process.env.VUE_APP_ENV_NAME}`;

const Trans = {
  get defaultLanguage() {
    return DEFAULT_LANGUAGE;
  },
  get supportedLanguages() {
    return SUPPORTED_LANGUAGES;
  },
  get currentLanguage() {
    return i18n.locale;
  },
  set currentLanguage(lang) {
    i18n.locale = lang;
  },

  /**
   * Gets the first supported language that matches the user's
   * @return {String}
   */
  getUserSupportedLang() {
    const userPreferredLang = Trans.getUserLang();
    // Check if user preferred browser lang is supported
    if (Trans.isLangSupported(userPreferredLang.lang)) {
      return userPreferredLang.lang;
    }
    // Check if user preferred lang without the ISO is supported
    if (Trans.isLangSupported(userPreferredLang.langNoISO)) {
      return userPreferredLang.langNoISO;
    }
    return Trans.defaultLanguage;
  },

  /**
   * Returns the users preferred language
   */
  getUserLang() {
    let path_lang = undefined;
    try {
      path_lang = location.pathname.split("/")[1];
      if (!this.isLangSupported(path_lang)) {
        path_lang = undefined;
      }
    } catch (e) {
      console.log("Error in getUserLang: ", e);
    }

    const rawLang = path_lang || window.navigator.language || Trans.defaultLanguage;
    let lang = rawLang;
    if (rawLang.indexOf("-") > 0) {
      const lowerLang = rawLang.toLowerCase();
      if (lowerLang === "zh-tw" || lowerLang === "zh-hk") {
        lang = "zh-TW";
      } else if (lowerLang === "zh-cn") {
        lang = "zh-CN";
      } else {
        lang = lang.substr(0, 2);
      }
    } else if (lang == "zh") {
      lang = "zh-CN";
    }
    return {
      lang: lang,
      langNoISO: lang.split("-")[0],
    };
  },

  /**
   * Sets the language to various services (axios, the html tag etc)
   * @param {String} lang
   * @return {String} lang
   */
  setI18nLanguageInServices(lang: string) {
    Trans.currentLanguage = lang;
    axios.defaults.headers.common["Accept-Language"] = lang;

    const element: HTMLHtmlElement | null = document.querySelector("html");
    element ? element.setAttribute("lang", lang) : null;

    const bodyElement = document.querySelector("body span") as HTMLBodyElement;
    if (lang === "my") {
      bodyElement.setAttribute("class", "myan");
    } else {
      bodyElement.removeAttribute("class");
    }

    return lang;
  },

  /**
   * Loads new translation messages and changes the language when finished
   * @param lang
   * @return {Promise<any>}
   */
  changeLanguage(lang: string) {
    if (!Trans.isLangSupported(lang)) return Promise.reject(new Error("Language not supported"));
    if (i18n.locale === lang) return Promise.resolve(lang); // has been loaded prior
    return Trans.loadLanguageFile(lang).then((data) => {
      const localeMessages = data.data;
      i18n.setLocaleMessage(lang, localeMessages.default || localeMessages);
      return Trans.setI18nLanguageInServices(lang);
    });
  },

  /**
   * Async loads a translation file
   * @param lang
   * @return {Promise<*>|*}
   */
  loadLanguageFile(lang: string) {
    console.log(`Load Language: ${lang}`);
    i18n.locale = lang;
    return axios.get(`${LANG_BASE_URL}/${lang}.json`);
  },

  /**
   * Checks if a lang is supported
   * @param {String} lang
   * @return {boolean}
   */
  isLangSupported(lang: string) {
    return Trans.supportedLanguages.includes(lang);
  },

  /**
   * Checks if the route's param is supported, if not, redirects to the first supported one.
   * @param {Route} to
   * @param {Route} from
   * @param {Function} next
   * @return {*}
   */
  routeMiddleware(to: any, from: any, next: any) {
    // Load async message files here
    const lang = to.params.lang;
    console.log(`***** ${lang} *****`);
    if (!Trans.isLangSupported(lang)) return next(Trans.getUserSupportedLang());
    return Trans.changeLanguage(lang).then(() => next());
  },

  /**
   * Returns a new route object that has the current language already defined
   * To be used on pages and components, outside of the main \ route, like on Headers and Footers.
   * @example <router-link :to="$i18nRoute({ name: 'someRoute'})">Click Me </router-link>
   * @param {Object} to - route object to construct
   */
  i18nRoute(to: Record<string, any>) {
    return {
      ...to,
      params: { lang: this.currentLanguage, ...to.params },
    };
  },
};

Trans.changeLanguage(Trans.getUserSupportedLang()).then((r) => console.log("Set first browser language"));

export { Trans };
